import { useMemo, useCallback } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { TechniqueImpact } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { MoreMenu, Typography, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { formatNumber, formatMetersToDistance, formatDate } from "@l2r-front/l2r-utils"

import { API_MODULE_ROADWORK } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { STATUS_DONE, STATUSES, statusesSettings } from "../../constants/status"
import { useRoadworkDelete } from "../../hooks/mutations/roadwork/useRoadworkDelete"
import { computeRoadworkLength } from "../../utils/roadworkUtils"

import * as Styled from "./TechniqueItemCard.styled"

type TechniqueItemCardProps = {
    currency: string
    doneAt?: string | number
    handleClick: () => void
    handleGoToDetails?: () => void
    handleComplete?: () => void
    technique: {
        id: string
        acronym: string
        cost: number
        impact: TechniqueImpact
        linearLocation?: {
            startMeasure: number
            endMeasure: number
        }[]
        year: number
    }
    status: typeof STATUSES[number]
    isSelected?: boolean
    isDisabled?: boolean
    isEditable?: boolean
    subNetworks?: string[]
}

export function TechniqueItemCard(props: TechniqueItemCardProps) {

    const {
        currency,
        doneAt,
        handleClick,
        handleComplete,
        handleGoToDetails,
        technique,
        status,
        isSelected = false,
        isDisabled = false,
        isEditable = true,
        subNetworks,
        ...otherProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { road } = useParams()
    const readOnly = useRoleIsReadOnly(subNetworks, API_MODULE_ROADWORK)
    const { setAlert } = useAlertsDispatchContext()
    const isMobile = useIsMobileDevice()

    const deleteMutation = useRoadworkDelete(technique, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkCard.snackbar.deleteError"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkCard.snackbar.deleteSuccess"),
    })

    const formattedTechnique = useMemo(() => {
        const techniqueLength = computeRoadworkLength(technique.linearLocation)
        return ({
            ...technique,
            cost: formatNumber(technique.cost),
            impact: technique.impact,
            length: technique.linearLocation ? formatMetersToDistance(techniqueLength) : null,
        })
    }, [technique])

    const {
        acronym,
        cost,
        impact,
        length,
        year,
    } = formattedTechnique

    const isDeletable = useMemo(() => {
        return isEditable && status !== STATUS_DONE && !readOnly
    }, [readOnly, status, isEditable])

    const handleDeleteEquipment = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.confirmation"),
            cancelText: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.confirm"),
            callback: () => {
                deleteMutation.mutate(technique)
            },
            open: true,
        })
    }, [deleteMutation, setAlert, t, technique])

    const handleDetailsButtonClick = useCallback(() => {
        if (handleGoToDetails) {
            handleGoToDetails()
        }
    }, [handleGoToDetails])

    const displayButtonsZone = handleGoToDetails || handleComplete

    return <Styled.Card
        role="button"
        elevation={0}
        {...otherProps}
        onClick={handleClick}
        disabled={isDisabled}
        selected={isSelected}>
        <Styled.ContentWrapper>
            <Styled.BadgeWrapper>
                {doneAt ? <Styled.DoneAtWrapper>
                    <Typography variant="Desktop & Mobile/Body/Small">{!road ? doneAt : formatDate(typeof doneAt === "number" ? String(doneAt) : doneAt, isMobile ? "yyyy" : "dd.MM.yyyy")}</Typography>
                </Styled.DoneAtWrapper> :
                    <Typography variant="Desktop & Mobile/Body/Small">{year}</Typography>
                }
                <Styled.HeaderRight>
                    <Styled.ColoredBadge id={"impact-badge"} impact={impact} />
                    {handleGoToDetails && <MoreMenu
                        color="tertiary"
                        size="medium"
                        menuConfig={[
                            {
                                id: "details-navigation",
                                displayed: true,
                                label: t(I18N_NAMESPACE, "containers.techniqueItemCard.menu.details"),
                                icon: null,
                                props: {
                                    onClick: handleDetailsButtonClick,
                                },
                            },
                            {
                                displayed: !!isDeletable,
                                id: "edit-delete",
                                label: t(I18N_NAMESPACE, "containers.techniqueItemCard.menu.delete"),
                                icon: null,
                                props: {
                                    onClick: handleDeleteEquipment,
                                },
                            },

                        ].filter(Boolean)}
                    />}
                </Styled.HeaderRight>
            </Styled.BadgeWrapper>
            <Styled.Element>
                <Typography variant="Desktop & Mobile/Body/Small accent">{acronym}</Typography>
            </Styled.Element>
            <Styled.BottomElementsWrapper>
                {length && <Styled.Element>
                    <Styled.TextWrapper>
                        <Typography variant="Desktop & Mobile/Heading/H3">{t(I18N_NAMESPACE, `components.techniqueCardItem.${isMobile ? "lengthMobile" : "length"}`)}</Typography>
                        <Typography variant="Desktop & Mobile/Body/Regular">{length}</Typography>
                    </Styled.TextWrapper>
                </Styled.Element>}
                <Styled.Element>
                    <Styled.TextWrapper>
                        <Typography variant="Desktop & Mobile/Heading/H3">{t(I18N_NAMESPACE, "components.techniqueCardItem.cost")}</Typography>
                        <Styled.Cost variant="Desktop & Mobile/Body/Regular">{`${cost} ${currency}`}</Styled.Cost>
                    </Styled.TextWrapper>
                </Styled.Element>
            </Styled.BottomElementsWrapper>
        </Styled.ContentWrapper>
        {displayButtonsZone && <Styled.Element>
            <Styled.ButtonsWrapper>
                <Styled.StatusChip label={t(I18N_NAMESPACE, statusesSettings[status as keyof typeof statusesSettings].statusLabel)} />
            </Styled.ButtonsWrapper>
        </Styled.Element>}
    </Styled.Card>
}
